// =====================================================
// PAGE: Mes Tâches
// Description: Page pour afficher les prospects, équipages et tâches assignés à l'utilisateur
// =====================================================

import { useState, useMemo, useCallback } from 'react';
import { Users, Repeat, CalendarClock } from 'lucide-react';
import { PageHeader } from '@/components/page/PageHeader';
import { StatisticCard } from '@/components/page/StatisticCard';
import { TabContent } from '@/components/page/TabContent';
import { UserTimeline } from '@/components/timeline/UserTimeline';
import { Card } from '@/components/ui/card';
import { TabNavigation } from '@/components/ui/TabNavigation';
import { EnterpriseTable } from '@/components/tables/EnterpriseTable';
import { useMyData } from '@/hooks/useMyData';
import { ProspectDetailPanel } from '@/components/panels/ProspectDetailPanel';
import { useNavigate } from 'react-router-dom';
import { createProspectColumns, createCrewColumns, createTasksColumns } from '@/components/tasks';
import { toast } from 'sonner';
import type { FormSubmission } from '@/types/form';
import type { Task } from '@/types/task';

type TaskEntityFilter = 'all' | 'contact' | 'crew' | 'prospect';

const TASK_ENTITY_FILTER_LABELS: Record<TaskEntityFilter, string> = {
  all: 'Toutes',
  crew: 'Équipages',
  prospect: 'Prospects',
  contact: 'Contacts',
};

/**
 * Page "Mes tâches" avec 3 onglets :
 * - Mes prospects (prospects assignés à l'utilisateur)
 * - Mes équipages (équipages assignés à l'utilisateur)
 * - Mes tâches (tâches assignées à l'utilisateur)
 */
function TasksPage() {
  const navigate = useNavigate();
  const { myProspects, myCrews, myTasks, stats, loading, deleteTask } = useMyData();
  const [activeTab, setActiveTab] = useState('prospects');

  // États de recherche pour chaque onglet
  const [prospectSearch, setProspectSearch] = useState('');
  const [crewSearch, setCrewSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  // Item #8 retours client : filtre par type d'entité dans l'onglet "Mes tâches"
  const [taskEntityFilter, setTaskEntityFilter] = useState<TaskEntityFilter>('all');

  // États pour le sidepanel prospects
  const [selectedProspect, setSelectedProspect] = useState<FormSubmission | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Configuration des onglets
  const tabs = useMemo(
    () => [
      { id: 'prospects', label: 'Mes prospects' },
      { id: 'crews', label: 'Mes équipages' },
      { id: 'tasks', label: 'Mes tâches' },
    ],
    []
  );

  // Handlers de navigation (avant les colonnes pour éviter les warnings)
  const handleViewProspect = useCallback(
    (prospectId: string) => {
      const prospect = myProspects.find((p) => p.id === prospectId);
      if (prospect) {
        setSelectedProspect(prospect);
        setIsSidePanelOpen(true);
      }
    },
    [myProspects]
  );

  const handleViewCrew = useCallback(
    (crewId: string) => {
      navigate(`/crews/${crewId}`);
    },
    [navigate]
  );

  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
  }, []);

  const handleUpdateProspect = useCallback(
    async (
      _prospectId: string,
      _contactId: string,
      _updates: {
        contact: Partial<any>;
        submission: { data: Record<string, any> };
      }
    ) => {
      // TODO: Implémenter la mise à jour du prospect
      // Rafraîchir les données si nécessaire
      // useMyData devrait se rafraîchir automatiquement
      await Promise.resolve(); // Éviter warning async
    },
    []
  );

  const handleDeleteProspect = useCallback(
    async (_prospectId: string) => {
      // TODO: Implémenter la suppression du prospect
      await Promise.resolve(); // Éviter warning async
      handleCloseSidePanel();
    },
    [handleCloseSidePanel]
  );

  // Colonnes générées avec les helpers
  const prospectColumns = useMemo(
    () => createProspectColumns(handleViewProspect),
    [handleViewProspect]
  );

  const crewColumns = useMemo(() => createCrewColumns(handleViewCrew), [handleViewCrew]);

  // Handler pour consulter l'entité d'une tâche
  const handleViewTask = useCallback(
    (task: Task) => {
      switch (task.entity_type) {
        case 'contact':
          navigate(`/contacts/${task.entity_id}`);
          break;
        case 'crew':
          navigate(`/crews/${task.entity_id}`);
          break;
        case 'prospect': {
          // Trouver le prospect et ouvrir le sidepanel
          const prospect = myProspects.find((p) => p.id === task.entity_id);
          if (prospect) {
            setSelectedProspect(prospect);
            setIsSidePanelOpen(true);
            // Changer l'onglet actif si on n'est pas déjà sur les prospects
            if (activeTab !== 'prospects') {
              setActiveTab('prospects');
            }
          } else {
            toast.error('Prospect introuvable', {
              description: 'Impossible de trouver le prospect associé à cette tâche',
            });
          }
          break;
        }
        default:
          toast.error("Type d'entité non supporté", {
            description: 'Impossible de consulter cette entité',
          });
      }
    },
    [navigate, myProspects, activeTab]
  );

  // Handler pour supprimer une tâche
  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      const success = await deleteTask(taskId);
      if (success) {
        toast.success('Tâche supprimée', {
          description: 'La tâche a été supprimée avec succès',
        });
      } else {
        toast.error('Erreur', {
          description: 'Impossible de supprimer la tâche',
        });
      }
    },
    [deleteTask]
  );

  const tasksColumns = useMemo(
    () => createTasksColumns(handleViewTask, handleDeleteTask),
    [handleViewTask, handleDeleteTask]
  );

  // Filtrer prospects par recherche
  const filteredProspects = useMemo(() => {
    if (!prospectSearch) return myProspects;
    const search = prospectSearch.toLowerCase();
    return myProspects.filter(
      (p) =>
        p.contact?.last_name?.toLowerCase().includes(search) ||
        p.contact?.first_name?.toLowerCase().includes(search) ||
        p.contact?.email?.toLowerCase().includes(search) ||
        p.form?.origin?.label?.toLowerCase().includes(search)
    );
  }, [myProspects, prospectSearch]);

  // Filtrer crews par recherche
  const filteredCrews = useMemo(() => {
    if (!crewSearch) return myCrews;
    const search = crewSearch.toLowerCase();
    return myCrews.filter(
      (c) =>
        c.name?.toLowerCase().includes(search) ||
        c.crew_number?.toLowerCase().includes(search) ||
        c.product?.name?.toLowerCase().includes(search)
    );
  }, [myCrews, crewSearch]);

  // Filtrer tâches par recherche d'abord (sert de base aux counts par type)
  const tasksMatchingSearch = useMemo(() => {
    if (!taskSearch) return myTasks;
    const search = taskSearch.toLowerCase();
    return myTasks.filter(
      (t) => t.title?.toLowerCase().includes(search) || t.notes?.toLowerCase().includes(search)
    );
  }, [myTasks, taskSearch]);

  // Item #8 : counts par type d'entité (basés sur la recherche, pas sur le type)
  const taskCountsByEntity = useMemo(() => {
    const counts: Record<TaskEntityFilter, number> = {
      all: tasksMatchingSearch.length,
      contact: 0,
      crew: 0,
      prospect: 0,
    };
    for (const task of tasksMatchingSearch) {
      counts[task.entity_type] += 1;
    }
    return counts;
  }, [tasksMatchingSearch]);

  // Item #8 : appliquer le filtre par type d'entité après la recherche
  const filteredTasks = useMemo(() => {
    if (taskEntityFilter === 'all') return tasksMatchingSearch;
    return tasksMatchingSearch.filter((t) => t.entity_type === taskEntityFilter);
  }, [tasksMatchingSearch, taskEntityFilter]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-zinc-500">
          <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-zinc-900"></div>
          <span>Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-neutral-50">
      {/* Breadcrumb - Toujours visible en haut, pleine largeur */}
      <PageHeader breadcrumbs={[{ label: 'Tableau de bord' }, { label: 'Mes tâches' }]} />

      {/* Titre - Pleine largeur */}
      <div className="border-t border-b border-zinc-200 bg-white px-6 py-6">
        <h1 className="text-xl font-semibold text-zinc-900">Mes tâches</h1>
      </div>

      {/* Layout horizontal : Gauche (Cards + Tabs + Tables) | Droite (Timeline + Sidepanel) */}
      <div className="flex flex-1 gap-6 overflow-hidden p-6">
        {/* Colonne gauche : 60% */}
        <div className="flex flex-1 flex-col gap-6 overflow-hidden">
          {/* Cards statistiques */}
          <div className="grid grid-cols-3 gap-6">
            <StatisticCard
              title="Mes équipages"
              value={stats.crewsCount}
              description={`${stats.prospectsCount} Prospects • ${stats.crewsCount} Équipages`}
              icon={Users}
            />
            <StatisticCard
              title="Taux conversion"
              value={`${stats.conversionRate}%`}
              description={`${stats.crewsCount} Équipages`}
              icon={Repeat}
            />
            <StatisticCard
              title="Tâches planifiées"
              value={stats.totalPendingTasks}
              description={`${stats.totalUrgentTasks} Urgentes`}
              icon={CalendarClock}
            />
          </div>

          {/* Navigation par onglets */}
          <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Card avec Tabs et Tables */}
          <Card className="flex flex-1 flex-col overflow-hidden">
            {/* Contenu des onglets */}
            <div className="flex-1 overflow-auto px-6">
              {activeTab === 'prospects' && (
                <TabContent
                  title="Mes prospects"
                  count={filteredProspects.length}
                  searchValue={prospectSearch}
                  searchPlaceholder="Rechercher un prospect..."
                  onSearchChange={setProspectSearch}
                  isEmpty={filteredProspects.length === 0}
                  emptyMessage="Aucun prospect assigné"
                  emptySearchMessage="Aucun prospect trouvé"
                >
                  <EnterpriseTable
                    data={filteredProspects}
                    columns={prospectColumns}
                    pageSize={1000}
                  />
                </TabContent>
              )}

              {activeTab === 'crews' && (
                <TabContent
                  title="Mes équipages"
                  count={filteredCrews.length}
                  searchValue={crewSearch}
                  searchPlaceholder="Rechercher un équipage..."
                  onSearchChange={setCrewSearch}
                  isEmpty={filteredCrews.length === 0}
                  emptyMessage="Aucun équipage assigné"
                  emptySearchMessage="Aucun équipage trouvé"
                >
                  <EnterpriseTable data={filteredCrews} columns={crewColumns} pageSize={1000} />
                </TabContent>
              )}

              {activeTab === 'tasks' && (
                <div className="flex flex-col gap-4">
                  {/* Item #8 : filtres rapides par type d'entité — toujours visibles */}
                  <div className="flex flex-wrap items-center gap-2">
                    {(Object.keys(TASK_ENTITY_FILTER_LABELS) as TaskEntityFilter[]).map((key) => {
                      const isActive = taskEntityFilter === key;
                      const count = taskCountsByEntity[key];
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setTaskEntityFilter(key)}
                          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                            isActive
                              ? 'border-zinc-900 bg-zinc-900 text-white'
                              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                          }`}
                        >
                          <span>{TASK_ENTITY_FILTER_LABELS[key]}</span>
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                              isActive ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-600'
                            }`}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <TabContent
                    title="Mes tâches"
                    count={filteredTasks.length}
                    searchValue={taskSearch}
                    searchPlaceholder="Rechercher une tâche..."
                    onSearchChange={setTaskSearch}
                    isEmpty={filteredTasks.length === 0}
                    emptyMessage={
                      taskEntityFilter === 'all'
                        ? 'Aucune tâche assignée'
                        : `Aucune tâche ${TASK_ENTITY_FILTER_LABELS[taskEntityFilter].toLowerCase()}`
                    }
                    emptySearchMessage="Aucune tâche trouvée"
                  >
                    <EnterpriseTable data={filteredTasks} columns={tasksColumns} pageSize={1000} />
                  </TabContent>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Colonne droite : 40% - Position relative pour le sidepanel */}
        <div className="relative w-[40%] overflow-hidden">
          <UserTimeline />

          {/* Sidepanel qui se superpose à la timeline (retiré du DOM quand fermé) */}
          {selectedProspect && isSidePanelOpen && (
            <div className="absolute inset-0 z-10">
              <ProspectDetailPanel
                isOpen={isSidePanelOpen}
                onClose={handleCloseSidePanel}
                prospect={selectedProspect}
                onUpdate={handleUpdateProspect}
                onDelete={handleDeleteProspect}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TasksPage;
